import { Injectable, OnDestroy, signal } from '@angular/core';

import { captureElement } from './element-capture';

/**
 * Capture de l'élément signalé et son aperçu, pour un signalement à la fois.
 *
 * Une capture qui se termine après qu'une autre a été lancée, ou après clear(), est ignorée :
 * fermer la popup ou resélectionner ne laisse pas une capture tardive remplir un autre signalement.
 * Un échec — image d'un autre domaine, élément détaché entre-temps — ne bloque pas le signalement :
 * le ticket part simplement sans image.
 */
@Injectable()
export class ScreenshotCapture implements OnDestroy {
  /** Lien de l'aperçu, null sans capture. */
  readonly url = signal<string | null>(null);
  readonly isCapturing = signal(false);

  private image: Blob | null = null;
  /** Numéro de la capture en cours, changé à chaque nouvelle capture et à chaque clear(). */
  private generation = 0;

  /** Image à joindre au ticket, null sans capture. */
  get blob(): Blob | null {
    return this.image;
  }

  async capture(element: Element): Promise<void> {
    const generation = ++this.generation;
    this.revoke();
    this.isCapturing.set(true);

    try {
      // Laisser la popup s'afficher avant le travail de capture, qui occupe le thread principal.
      await new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve)));
      if (generation !== this.generation) {
        return;
      }

      const blob = await captureElement(element);
      if (blob && generation === this.generation) {
        this.image = blob;
        this.url.set(URL.createObjectURL(blob));
      }
    } catch (error) {
      console.warn('Bug report screenshot failed:', error);
    } finally {
      if (generation === this.generation) {
        this.isCapturing.set(false);
      }
    }
  }

  /** Oublie la capture, et celle qui est en cours. */
  clear(): void {
    this.generation++;
    this.isCapturing.set(false);
    this.revoke();
  }

  ngOnDestroy(): void {
    this.clear();
  }

  private revoke(): void {
    const url = this.url();
    if (url) {
      URL.revokeObjectURL(url);
    }
    this.url.set(null);
    this.image = null;
  }
}
